// CB:BEGIN admin-contractor-view
import React, { useState, useEffect } from 'react';
import { useApi } from '@/lib/useApi';
import ContractorDashboardView from '../../components/admin/ContractorDashboardView';
import { useImpersonation } from '../../context/ImpersonationContext';

interface AdminContractorViewProps {
  contractorId: string;
  onNavigate: (screen: string) => void;
}

const AdminContractorView: React.FC<AdminContractorViewProps> = ({ contractorId, onNavigate }) => {
  const api = useApi();
  const { enterSession, isImpersonating, impersonatedUser } = useImpersonation();
  const [contractor, setContractor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  useEffect(() => {
    fetchContractor();
  }, [contractorId]);

  const fetchContractor = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/api/admin/contractors/${contractorId}`);
      setContractor(data);
    } catch (err) {
      console.error('Error fetching contractor:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch contractor');
    } finally {
      setLoading(false);
    }
  };

  const handleEnterSession = async () => {
    if (!contractor?.user?.id) return;
    setSessionLoading(true);
    setSessionError(null);
    try {
      await enterSession(contractor.user.id);
      // Session active — banner shows automatically
    } catch (err: any) {
      setSessionError(err.message || 'Failed to enter session');
    } finally {
      setSessionLoading(false);
    }
  };

  const alreadyInThisSession =
    isImpersonating &&
    impersonatedUser?.contractorId === contractorId;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        <p style={{ marginTop: 16, color: '#6b7280' }}>Loading contractor dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#dc2626' }}>
        <p>Error loading contractor: {error}</p>
        <button onClick={fetchContractor} className="btn btn-primary" style={{ marginTop: 16 }}>
          Retry
        </button>
      </div>
    );
  }

  const name = contractor?.company || contractor?.user?.email || 'Contractor';

  return (
    <div>
      {/* Admin toolbar */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="btn btn-secondary"
            onClick={() => onNavigate('admin-contractors-list')}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            Back to Contractors
          </button>
          <span style={{ color: '#374151', fontWeight: 600, fontSize: 15 }}>
            {name}
          </span>
          {contractor?.user?.email && name !== contractor.user.email && (
            <span style={{ color: '#6b7280', fontSize: 13 }}>{contractor.user.email}</span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {sessionError && (
            <span style={{ color: '#dc2626', fontSize: 12 }}>{sessionError}</span>
          )}
          {alreadyInThisSession ? (
            <span style={{
              padding: '6px 12px',
              background: '#fef3c7',
              color: '#92400e',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
            }}>
              Session active — banner visible above
            </span>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleEnterSession}
              disabled={sessionLoading || !contractor?.user?.id}
              style={{ fontSize: 13, padding: '6px 14px' }}
              title="Enter this contractor's session to take actions on their behalf"
            >
              {sessionLoading ? 'Entering...' : 'Enter Contractor Session'}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => onNavigate(`admin-contractor-edit-${contractorId}`)}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            Edit Data
          </button>
        </div>
      </div>

      {/* Contractor dashboard view */}
      <ContractorDashboardView contractorId={contractorId} onNavigate={onNavigate} />
    </div>
  );
};

export default AdminContractorView;
// CB:END admin-contractor-view
